// - Primary error logging service for the application (AutomatedErrorSystem)
// - Used by components, hooks, and services to log errors directly to Firestore
// v1.1: Add debug_mode via context.debug; step-by-step logging when enabled

import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

import { db } from '../../../services/firebase.js';

function debugLog(debug, ...a) {
  if (debug) console.log('[RawErrorService]', ...a);
}

/**
 * Generate 4 random digits for doc ID suffix
 * @returns {string}
 */
function rnd4() {
  return String(Math.floor(1000 + Math.random() * 9000));
}

/**
 * Format date as yyyymmddhhmmss
 * @returns {string}
 */
function yyyymmddhhmmss() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${y}${m}${day}${h}${min}${s}`;
}

/**
 * Simple hash for error messages to group similar errors
 * @param {string} msg 
 * @returns {string}
 */
function hashMessage(msg) {
  let h = 0;
  const s = (msg || '').substring(0, 200);
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return String(Math.abs(h));
}

/**
 * Write a raw error doc. Path: Logging/Errors/raw/{year}/{month}/{day}/items (7 segments = collection).
 * Doc ID: {moduleName}_{rnd4}_{yyyymmddhhmmss}. Module name only (e.g. DialogueUniversal).
 * @param {Error} error - Caught error
 * @param {Object} context - Same shape as ErrorLoggingService.logError context
 * @param {string} context.componentName - Component name only (used in doc ID)
 * @param {number} [context.level]
 * @param {number} [context.chapter]
 * @returns {Promise<string|null>} Doc ID if written, null on failure
 */
export async function writeRawError(error, context = {}) {
  const debug = !!context.debug;
  const moduleName = (context.componentName || 'Unknown').trim();
  const ts = yyyymmddhhmmss();
  const docId = `${moduleName}_${rnd4()}_${ts}`;

  const now = new Date();
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  debugLog(debug, '[STEP 1] Build path', { year, month, day, docId, path: `Logging/Errors/raw/${year}/${month}/${day}/items/${docId}` });

  const errorMsg = error?.message || 'Unknown Error';
  const rawData = {
    message: errorMsg,
    message_hash: hashMessage(errorMsg),
    name: error?.name || 'Error',
    code: error?.code || null,
    stack: error?.stack || null,
    severity: context.severity || 'critical',
    operationType: context.operationType || 'component_error',
    componentStack: context.componentStack || null,
    url: typeof window !== 'undefined' ? window.location.href : 'N/A',
    isValidationError: context.isValidationError || false,
    validationErrors: context.validationErrors || [],
    componentName: moduleName,
    level: context.level ?? null,
    chapter: context.chapter ?? null,
    timestamp: serverTimestamp(),
    createdAt: new Date().toISOString(),
    version: '1.0'
  };

  try {
    debugLog(debug, '[STEP 2] setDoc', { docId });
    const docRef = doc(db, 'Logging', 'Errors', 'raw', year, month, day, 'items', docId);
    await setDoc(docRef, rawData);
    debugLog(debug, '[STEP 3] setDoc success', { docId });
    console.group('RawErrorService: Raw error written');
    console.log('Doc ID:', docId);
    console.log('Path: Logging/Errors/raw/%s/%s/%s/items/%s', year, month, day, docId);
    console.log('Component:', moduleName);
    console.groupEnd();
    return docId;
  } catch (e) {
    console.error('RawErrorService: Failed to write raw error', e);
    console.error('Original error:', error?.message);
    return null;
  }
}

/**
 * Global error helper: write a failureContext (from unhandledrejection, network, route validation, etc.)
 * into the same pipeline as component errors. Builds an Error-like object and context, then calls writeRawError.
 * @param {Object} failureContext - e.g. { type, errorType, component, error, message, url, ... }
 * @returns {Promise<string|null>} Doc ID if written, null on failure
 */
export async function writeRawErrorFromFailureContext(failureContext = {}) {
  const msg =
    failureContext.message ||
    (failureContext.error && (failureContext.error.message || String(failureContext.error))) ||
    failureContext.errorType ||
    failureContext.type ||
    'Unknown error';
  const err =
    failureContext.error instanceof Error
      ? failureContext.error
      : new Error(msg);
  const context = {
    componentName: failureContext.component || 'system',
    severity: 'critical',
    operationType: failureContext.type || 'global_error',
    url: failureContext.url || (typeof window !== 'undefined' ? window.location.href : 'N/A')
  };
  if (failureContext.level != null) context.level = failureContext.level;
  if (failureContext.chapter != null) context.chapter = failureContext.chapter;
  return writeRawError(err, context);
}

/**
 * Log an admin action as a ticket for Auto Debugger audit trail.
 * Writes to same raw path as errors; use operationType: 'admin_action' and severity: 'info'.
 * @param {string} componentName - Component (e.g. EmailBoard, MarketingScreen)
 * @param {string} action - Action name (e.g. refresh_inbox, send_email, run_ingestion)
 * @param {Object} [details] - Optional extra data
 * @returns {Promise<string|null>} Doc ID if written, null on failure
 */
export async function writeAdminActionTicket(componentName, action, details = {}) {
  const err = new Error(`Admin action: ${action}`);
  const context = {
    componentName: (componentName || 'Admin').trim(),
    operationType: 'admin_action',
    severity: 'info',
    ...details
  };
  return writeRawError(err, context);
}

const rawErrorServiceDefault = { writeRawError, writeRawErrorFromFailureContext, writeAdminActionTicket };
export default rawErrorServiceDefault;
